import { useState } from 'react';
import { comparisonAPI, quoteAPI } from '../services/api';

/**
 * Custom hook for quote comparison state management
 */
const useQuoteComparison = () => {
  const [comparison, setComparison] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [evidenceModal, setEvidenceModal] = useState(null);

  /**
   * Fetch comparison from API
   */
  const fetchComparison = async (shipmentId, comparisonId = null) => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (comparisonId) {
        // Fetch existing comparison by ID
        response = await comparisonAPI.get(comparisonId);
        setComparison(response.data.comparison);
        setQuotes(response.data.quotes || []);
      } else {
        // Create new comparison for shipment
        response = await comparisonAPI.compare(shipmentId);
        setComparison(response.data);

        // Fetch full quote details
        const quoteIds = response.data.scores.map(s => s.quoteId);
        const quotePromises = quoteIds.map(id => quoteAPI.get(id));
        const quoteResponses = await Promise.all(quotePromises);
        setQuotes(quoteResponses.map(r => r.data));
      }
    } catch (err) {
      console.error('Error fetching comparison:', err);
      const errorMessage = err.response?.data?.error || 'Failed to load comparison';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Select a quote manually
   */
  const selectQuote = async (quoteId, reason) => {
    try {
      setLoading(true);
      setError(null);

      // Get shipment ID from comparison
      const shipmentId = comparison.shipmentId || comparison.comparison?.shipmentId;

      await comparisonAPI.select(shipmentId, quoteId, {
        reason,
        selectedBy: 'operator' // TODO: Get from auth context
      });

      return true;
    } catch (err) {
      console.error('Error selecting quote:', err);
      const errorMessage = err.response?.data?.error || 'Failed to select quote';
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto-select recommended quote
   */
  const autoSelect = async () => {
    const recommendedQuoteId = comparison.recommendedQuoteId || comparison.comparison?.recommendedQuoteId;
    const reasoning = comparison.reasoning || comparison.comparison?.reasoning;

    if (!recommendedQuoteId) {
      setError('No recommended quote available');
      return false;
    }

    return await selectQuote(
      recommendedQuoteId,
      `Auto-selected: ${reasoning}`
    );
  };

  /**
   * Confirm booking for a quote
   */
  const confirmBooking = async (quoteId) => {
    try {
      setLoading(true);
      setError(null);

      await quoteAPI.confirm(quoteId, 'EMAIL');

      alert('Booking confirmation sent to provider!');
      return true;
    } catch (err) {
      console.error('Error confirming booking:', err);
      const errorMessage = err.response?.data?.error || 'Failed to confirm booking';
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle score breakdown expansion
   */
  const toggleBreakdown = (quoteId) => {
    setExpandedQuote(expandedQuote === quoteId ? null : quoteId);
  };

  /**
   * Open evidence viewer modal
   */
  const viewEvidence = (quoteId) => {
    setEvidenceModal(quoteId);
  };

  /**
   * Close evidence viewer modal
   */
  const closeEvidence = () => {
    setEvidenceModal(null);
  };

  return {
    comparison,
    quotes,
    loading,
    error,
    expandedQuote,
    evidenceModal,
    fetchComparison,
    selectQuote,
    autoSelect,
    confirmBooking,
    toggleBreakdown,
    viewEvidence,
    closeEvidence
  };
};

export default useQuoteComparison;
